import { Alert, Badge, Card, Spinner, Table } from 'react-bootstrap';
import { useArchivedOrders } from '../../features/orders/useOrders';

/**
 * Orders that were deleted, kept.
 *
 * A deleted order is archived to MongoDB rather than dropped, the same way a
 * deleted product is — because "what happened to that order" is a question a
 * customer asks months later, and Redis has no memory of anything it no longer
 * holds.
 *
 * The release summary is shown alongside: how many metres went back to stock and
 * which products were freed. That is the part somebody chasing a discrepancy
 * actually needs.
 */

const fmt = n => Number(n || 0).toLocaleString();

const when = value => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : new Intl.DateTimeFormat('ro-RO', { dateStyle: 'medium', timeStyle: 'short' }).format(d);
};

const DeletedOrders = () => {
  const { data, isLoading, isError, error } = useArchivedOrders(200);
  const orders = data?.orders ?? [];

  return (
    <div className="pb-9">
      <h2 className="mb-1">Deleted orders</h2>
      <p className="text-700 mb-4">
        Archived when deleted. What they had promised was released back to stock at that
        moment — the summary below says how much.
      </p>

      <Card>
        <Card.Body>
          {isError ? (
            <Alert variant="danger" className="mb-0">
              {error?.response?.data?.error || error?.message || 'Could not load archived orders'}
            </Alert>
          ) : isLoading ? (
            <div className="d-flex justify-content-center py-6">
              <Spinner animation="border" />
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-6 text-700">No order has been deleted.</div>
          ) : (
            <div className="table-responsive">
              <Table size="sm" className="fs-9 mb-0">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Client</th>
                    <th className="text-end">Lines</th>
                    <th className="text-end">Ordered</th>
                    <th className="text-end">Returned to stock</th>
                    <th>Deleted</th>
                    <th>By</th>
                    <th>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => {
                    const lines = Array.isArray(o.lines) ? o.lines : [];
                    const ordered = lines.reduce(
                      (s, l) => s + (Number(l.totalOrderedLength) || 0),
                      0
                    );
                    const rel = o._releaseSummary || {};
                    return (
                      <tr key={o._id || o.orderId}>
                        <td className="fw-semibold">{o.orderCode || o.orderId}</td>
                        <td>{o.client || '—'}</td>
                        <td className="text-end">{lines.length}</td>
                        <td className="text-end">{fmt(ordered)} m</td>
                        <td className="text-end">
                          {fmt(rel.freedMeters)} m
                          {rel.freedJobs?.length ? (
                            <Badge bg="info" variant="phoenix" className="ms-2">
                              {rel.freedJobs.length} product(s)
                            </Badge>
                          ) : null}
                          {rel.warehouse?.freed ? (
                            <Badge bg="warning" variant="phoenix" className="ms-1">
                              {rel.warehouse.freed} drum(s)
                            </Badge>
                          ) : null}
                        </td>
                        <td>{when(o._archivedAt)}</td>
                        <td>{o._archivedBy || 'System'}</td>
                        <td className="text-700">{o._archivedReason || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default DeletedOrders;
